/* RESPONSIBLE TEAM: team-help-desk-experience */
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import storage from 'embercom/vendor/intercom/storage';
import type Session from 'embercom/services/session';

export default class ConversationActiveParticipant extends Service {
  @service declare session: Session;
  @service declare intercomEventService: $TSFixMe;

  @tracked private activeParticipantMap: Record<string, string> = {};

  constructor() {
    super(...arguments);
    // We don't preload all conversation active participants here
    // They will be loaded on-demand when getActiveParticipantId is called
  }

  activeParticipantKey(conversationId: string) {
    let appId = this.session.workspace.id;
    let adminId = this.session.teammate.id;
    return `${appId}::${adminId}::${conversationId}::ActiveParticipant`;
  }

  getActiveParticipantId(conversationId: string): string | null {
    if (!conversationId) {
      return null;
    }

    // Check if we already have this in memory
    if (this.activeParticipantMap[conversationId]) {
      return this.activeParticipantMap[conversationId];
    }

    // Otherwise fetch from storage
    let participantId = storage.get(this.activeParticipantKey(conversationId));

    // Cache in memory for faster access next time
    if (participantId) {
      this.activeParticipantMap[conversationId] = participantId;
    }

    return participantId;
  }

  @action setActiveParticipantId(conversationId: string, participantId: string): void {
    if (!conversationId || !participantId) {
      return;
    }

    // Update storage
    storage.set(this.activeParticipantKey(conversationId), participantId);

    // Update in-memory cache
    this.activeParticipantMap[conversationId] = participantId;

    // Track analytics event
    this.intercomEventService.trackAnalyticsEvent({
      action: 'selected',
      object: 'participant',
      section: 'conversation_details',
      context: 'conversation_details_sidebar',
      metadata: {
        conversation_id: conversationId,
        participant_id: participantId,
      },
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    conversationActiveParticipant: ConversationActiveParticipant;
    'conversation-active-participant': ConversationActiveParticipant;
  }
}
